/**
 * ehr#624 - the browser tab title: `[Module] · Credify`, the module name copied from the left nav label,
 * a middle dot (U+00B7) with one space each side. The title is static: it names the module only, never a
 * client, record or note, so opening a chart doesn't change it.
 *
 * The EHR has no router (the URL stays `/`), so App sets `document.title` from this one map whenever the
 * page it shows changes, and index.html's `<title>` is the landing page's title for a cold load.
 */

export const TITLE_SUFFIX = ' · Credify';

const t = (label: string): string => `${label}${TITLE_SUFFIX}`;

/**
 * Page key (App's `active`, or a pseudo key below) → tab title. Schedule, Form Builder and Messages open
 * their own sites; they are here so the label is the same wherever it's read. Billing and Reports also
 * have in-EHR pages (an Alerts Hub link can open them), titled with their nav label.
 */
export const MODULE_TITLES: Record<string, string> = {
  dashboard: t('Dashboard'),
  schedule: t('Schedule'),
  contacts: t('Contacts'),
  notes: t('Progress Notes'),
  forms_hub: t('Forms Hub'),
  form_builder: t('Form Builder'),
  tasks: t('Tasks'),
  messages: t('Messages'),
  billing: t('Billing'),
  reports: t('Reports'),
  settings: t('Settings'),
  // Audit log sits in the account menu beside Manage users, an admin page: titled as Settings.
  audit: t('Settings'),
  // Pages that aren't a nav module.
  sign_in: t('Sign In'),
  not_found: t('Not Found'),
};

/** Fallback for a page with no module (the bell's Notification log). */
export const DEFAULT_TITLE = 'Credify';

export const titleFor = (key: string | null | undefined): string =>
  (key && MODULE_TITLES[key]) || DEFAULT_TITLE;

/**
 * Which key names the tab right now. Signed out → Sign In. A Settings window (the Settings menu, Data &
 * settings, Manage Users and the other windows opened from it) → Settings, over whatever page is behind
 * it. Otherwise the page shown. Opening a client's chart keeps `active` on Contacts, so the title holds.
 */
export function titleKey(state: {
  signedIn: boolean;
  active: string;
  inSettings: boolean;
}): string {
  if (!state.signedIn) return 'sign_in';
  if (state.inSettings) return 'settings';
  return state.active;
}

/** Set the tab title for the current state (no-op outside a browser). */
export function applyTitle(state: {
  signedIn: boolean;
  active: string;
  inSettings: boolean;
}): void {
  if (typeof document === 'undefined') return;
  const next = titleFor(titleKey(state));
  if (document.title !== next) document.title = next;
}
